import type { CustomerDetails } from '@eti/schema-types';
import type { FlightInformationType, RegionType, SegmentsType } from '../../../../../models';
import { transformFlightInformationToRecord } from '../../../../../utils';

type SegmentGroupType = {
  destination: RegionType;
  origin: RegionType;
  arrivedAt: string;
  departuredAt: string;
  email: string;
  stops: RegionType[];
  carrierBookingNumber?: {
    bookingNumber?: string | null;
    carrierCode?: string | null;
    carrierName?: string | null;
  } | null;
};

type SegmentGroupFormattedType = {
  destination: RegionType;
  origin: RegionType;
  arrivedAt: string;
  departuredAt: string;
  email: string;
  stops: RegionType[];
};

const segmentsGroupedByBookingNumberAndCarrierCode = (
  segments: SegmentsType,
  flightInfoRecord: Record<string, FlightInformationType[number]>,
  customerDetails: CustomerDetails,
): Record<string, SegmentGroupType> =>
  segments.reduce((acc: Record<string, SegmentGroupType>, segment) => {
    if (segment?.__typename !== 'TripSegment') {
      return acc;
    }

    if (segment.pnr && customerDetails?.email) {
      const key =
        (segment.carrierBookingNumber?.carrierCode || segment.operatingCarrier.code) +
        (segment.carrierBookingNumber?.bookingNumber || '_not_available');
      const pnrFlightInfo = flightInfoRecord[segment.pnr];
      const shouldUseAliasEmail =
        !pnrFlightInfo.externalFlightVerificationInfo.isExternalFlightVerified &&
        Boolean(pnrFlightInfo.loginEmail);
      const email = shouldUseAliasEmail ? pnrFlightInfo.loginEmail!! : customerDetails.email;

      if (acc[key]) {
        acc[key].destination = segment.destination;
        acc[key].arrivedAt = segment.arrivedAt;
        acc[key].stops.push(segment.origin);
      } else {
        acc[key] = {
          destination: segment.destination,
          origin: segment.origin,
          arrivedAt: segment.arrivedAt,
          departuredAt: segment.departuredAt,
          carrierBookingNumber: segment.carrierBookingNumber || {
            carrierName: segment.operatingCarrier.name,
            carrierCode: segment.operatingCarrier.code,
            bookingNumber: null,
          },
          stops: [],
          email,
        };
      }
    }

    return acc;
  }, {});

export const getSegmentGroups = (
  segments: SegmentsType,
  customerDetails: CustomerDetails,
  flightInformation: FlightInformationType,
): SegmentGroupFormattedType[] => {
  const flightInfoRecord = transformFlightInformationToRecord(flightInformation);

  return Object.values(
    segmentsGroupedByBookingNumberAndCarrierCode(segments, flightInfoRecord, customerDetails),
  );
};
